import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { useTeams } from '@/context/TeamContext';
import { TID } from '@/constants/testIds';
import { Tv, Brain, ChevronRight, Loader, Volume2, VolumeX, Shield } from 'lucide-react';
import { primeTTS, cancelTTS } from '@/lib/tts';

const DEFENSES = [
  { id: 'man', tk: 'def_man' },
  { id: 'zone 2-3', tk: 'def_zone_2_3' },
  { id: 'zone 3-2', tk: 'def_zone_3_2' },
  { id: 'zone 1-3-1', tk: 'def_zone_1_3_1' },
  { id: 'switch', tk: 'def_switch' },
  { id: 'press', tk: 'def_press' },
];

const MOMENTS = [
  { id: 'half-court', tk: 'moment_half_court' },
  { id: 'ato', tk: 'moment_ato' },
  { id: 'blob', tk: 'moment_blob' },
  { id: 'slob', tk: 'moment_slob' },
  { id: 'endgame', tk: 'moment_endgame' },
];

export default function LiveGame() {
  const { t, lang } = useLang();
  const { activeTeam } = useTeams();
  const [defense, setDefense] = useState('man');
  const [moment, setMoment] = useState('half-court');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [narrate, setNarrate] = useState(() => {
    try { return window.localStorage?.getItem('pm_narrate') === '1'; } catch { return false; }
  });

  const toggleNarrate = () => {
    const next = !narrate;
    setNarrate(next);
    try { window.localStorage?.setItem('pm_narrate', next ? '1' : '0'); } catch { /* ignore */ }
    if (next) primeTTS(lang);
    else cancelTTS();
  };

  const recommend = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post('/ai/recommend', {
        defense, moment, language: lang,
        // F8: scope recommendations to the active team if present
        team_id: activeTeam?.id || undefined,
      });
      setResult(r.data);
    } catch {} finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_live')}</div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
            <Tv className="w-9 h-9 text-gold" /> {t('live_h1')}
          </h1>
          <button
            onClick={toggleNarrate}
            data-testid="live-narrate-toggle"
            title={narrate ? t('voice_on_hint') : t('voice_off_hint')}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md border-2 font-h uppercase tracking-wider text-xs transition ${
              narrate
                ? 'bg-gold/15 border-gold text-gold animate-glow-pulse'
                : 'border-gold/40 text-gold hover:bg-gold/10'
            }`}
          >
            {narrate ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            {narrate ? t('live_voice_on') : t('live_voice_off')}
          </button>
        </div>
        <p className="text-gray-400 mt-2">
          {t('live_subtitle')}
          {narrate && <span className="text-gold ms-1">{t('live_voice_extra')}</span>}
        </p>
        {activeTeam && (
          <div className="mt-3 inline-flex items-center gap-2 bg-gold/10 border border-gold/40 text-gold rounded-full px-3 py-1 text-xs font-mono uppercase tracking-widest">
            <Shield className="w-3.5 h-3.5" /> {t('team_active')}: {activeTeam.name}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">{t('defense')}</div>
          <div className="space-y-1.5">
            {DEFENSES.map(d => (
              <button
                key={d.id}
                data-testid={`${TID.liveDefenseSelect}-${d.id}`}
                onClick={() => setDefense(d.id)}
                className={`w-full text-left px-3 py-2.5 rounded border transition font-h uppercase tracking-wider text-sm ${
                  defense === d.id ? 'border-gold bg-gold/10 text-gold' : 'border-steel text-gray-300 hover:border-gold/60'
                }`}
              >{t(d.tk)}</button>
            ))}
          </div>
        </div>

        <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">{t('moment')}</div>
          <div className="space-y-1.5">
            {MOMENTS.map(m => (
              <button
                key={m.id}
                data-testid={`${TID.liveMomentSelect}-${m.id}`}
                onClick={() => setMoment(m.id)}
                className={`w-full text-left px-3 py-2.5 rounded border transition font-h uppercase tracking-wider text-sm ${
                  moment === m.id ? 'border-gold bg-gold/10 text-gold' : 'border-steel text-gray-300 hover:border-gold/60'
                }`}
              >{t(m.tk)}</button>
            ))}
          </div>
        </div>

        <div className="bg-card-steel border-2 border-gold rounded-lg p-5 flex flex-col">
          <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">{t('nav_ai')}</div>
          <div className="text-white text-sm mb-4">
            {t('live_defense_label')} <span className="text-gold font-mono">{defense}</span><br />
            {t('live_moment_label')} <span className="text-gold font-mono">{moment}</span>
          </div>
          <button
            data-testid={TID.liveRecommendBtn}
            onClick={recommend}
            disabled={loading}
            className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light disabled:opacity-50 animate-glow-pulse"
          >
            {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
            {t('recommend')}
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-6">
          <div className="bg-gold/10 border border-gold/50 rounded-md p-4 mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-gold mb-1">{t('live_justification')}</div>
            <p className="text-gray-100">{result.justification}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(result.plays || []).map((p, i) => (
              <Link
                key={p.id}
                to={`/playbook/${p.id}`}
                className="bg-card-steel border-2 border-gold/40 rounded-lg p-5 hover-lift block"
              >
                <div className="text-xs font-mono uppercase tracking-widest text-gold mb-1">#{i + 1} · {p.category}</div>
                <div className="font-h uppercase text-2xl text-white mb-2">{p.name}</div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">{p.subcategory}</span>
                  <span className="text-gold font-mono">{Math.round(p.success_rate * 100)}%</span>
                </div>
                <div className="mt-3 text-gold text-xs uppercase tracking-widest flex items-center gap-1">
                  {t('live_see_detail')} <ChevronRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
